/**
 * Integration Picker Platform Registry (Story 4.4)
 *
 * Single source of truth for all platform metadata used in the Integration Picker UI.
 * Values manually copied from ADAPTER_META (packages/integrations/src/registry.ts)
 * to avoid importing server-side dependencies into the frontend.
 *
 * Sync verified by test in packages/integrations workspace (test 8.18).
 */

import type { IntegrationProvider } from "@provenleap/types";

/**
 * Picker-specific category type aligned with landing page groupings.
 * NOT the same as IntegrationCategory from packages/integrations — different taxonomy.
 */
export type PickerCategory =
  | "Google"
  | "Social Media"
  | "Advertising"
  | "SEO"
  | "Email & Commerce"
  | "Call Tracking"
  | "Reviews"
  | "CRM";

export interface PickerPlatform {
  platform: string;
  displayName: string;
  description: string;
  domain: string;
  category: PickerCategory;
  isActive: boolean;
}

/**
 * All 30 platforms for the Integration Picker.
 * 14 active (from ADAPTER_META) + 16 coming-soon.
 *
 */
export const PICKER_PLATFORMS: PickerPlatform[] = [
  // ── Google (6 active) ──────────────────────────────────────────────
  {
    platform: "google-analytics-4",
    displayName: "Google Analytics 4",
    description: "Website traffic, user behavior, and conversion analytics",
    domain: "analytics.google.com",
    category: "Google",
    isActive: true,
  },
  {
    platform: "google-search-console",
    displayName: "Google Search Console",
    description: "Search performance, indexing status, and keyword rankings",
    domain: "search.google.com",
    category: "Google",
    isActive: true,
  },
  {
    platform: "google-ads",
    displayName: "Google Ads",
    description: "Ad campaigns, spend, conversions, and ROAS metrics",
    domain: "ads.google.com",
    category: "Google",
    isActive: true,
  },
  {
    platform: "google-business-profile",
    displayName: "Google Business Profile",
    description: "Local business visibility, reviews, and search impressions",
    domain: "business.google.com",
    category: "Google",
    isActive: true,
  },
  {
    platform: "youtube-analytics",
    displayName: "YouTube Analytics",
    description: "Video performance, audience engagement, and channel growth",
    domain: "youtube.com",
    category: "Google",
    isActive: true,
  },
  {
    platform: "google-pagespeed",
    displayName: "Google PageSpeed",
    description: "Core Web Vitals, performance scores, and optimization insights",
    domain: "pagespeed.web.dev",
    category: "Google",
    isActive: true,
  },

  // ── Social Media (7 active: 6 from landing page + instagram-ads) ──
  {
    platform: "facebook-pages",
    displayName: "Facebook Pages",
    description: "Page reach, engagement, follower growth, and post performance",
    domain: "facebook.com",
    category: "Social Media",
    isActive: true,
  },
  {
    platform: "facebook-ads",
    // Share the Facebook Pages logo (domain: "facebook.com") — same brand mark
    // app-wide; the dedicated facebook-ads icon was a different glyph.
    displayName: "Facebook Ads",
    description: "Ad campaigns, audience targeting, spend, and conversions",
    domain: "facebook.com",
    category: "Social Media",
    isActive: true,
  },
  {
    platform: "instagram-business",
    displayName: "Instagram Business",
    description: "Post reach, story views, follower demographics, and engagement",
    domain: "instagram.com",
    category: "Social Media",
    isActive: true,
  },
  {
    platform: "instagram-ads",
    displayName: "Instagram Ads",
    description: "Instagram ad campaigns, story ads, and shopping ad performance",
    domain: "instagram.com",
    category: "Social Media",
    isActive: true,
  },
  {
    platform: "linkedin-pages",
    displayName: "LinkedIn Pages",
    description: "Company page analytics, follower growth, and post engagement",
    domain: "linkedin.com",
    category: "Social Media",
    isActive: true,
  },
  {
    platform: "linkedin-ads",
    displayName: "LinkedIn Ads",
    description: "LinkedIn ad campaigns, lead gen forms, and B2B conversions",
    domain: "linkedin.com",
    category: "Social Media",
    isActive: true,
  },
  {
    platform: "tiktok-business",
    displayName: "TikTok Business",
    description: "Video performance, audience analytics, and ad campaign metrics",
    domain: "tiktok.com",
    category: "Social Media",
    isActive: true,
  },

  // ── Email & Commerce (2 active + 1 coming-soon) ─────────────────
  {
    platform: "mailchimp",
    displayName: "Mailchimp",
    description: "Email campaigns, open rates, click rates, and subscriber growth",
    domain: "mailchimp.com",
    category: "Email & Commerce",
    isActive: true,
  },
  {
    platform: "klaviyo",
    displayName: "Klaviyo",
    description: "Email & SMS marketing, flows, revenue attribution, and audience segments",
    domain: "klaviyo.com",
    category: "Email & Commerce",
    isActive: true,
  },
  {
    platform: "shopify",
    displayName: "Shopify",
    description: "Store revenue, orders, product performance, and conversion rates",
    domain: "shopify.com",
    category: "Email & Commerce",
    isActive: true,
  },

  // ── Advertising (5 coming-soon) ───────────────────────────────────
  {
    platform: "microsoft-ads",
    displayName: "Microsoft Ads",
    description: "Bing search ads, audience campaigns, and conversion tracking",
    domain: "ads.microsoft.com",
    category: "Advertising",
    isActive: true,
  },
  {
    platform: "pinterest-ads",
    displayName: "Pinterest Ads",
    description: "Promoted pins, shopping ads, and visual discovery campaigns",
    domain: "pinterest.com",
    category: "Advertising",
    isActive: true,
  },
  {
    platform: "snapchat-ads",
    displayName: "Snapchat Ads",
    description: "Snap ads, story ads, and audience engagement metrics",
    domain: "snapchat.com",
    category: "Advertising",
    isActive: true,
  },
  {
    platform: "reddit-ads",
    displayName: "Reddit Ads",
    description: "Promoted posts, community targeting, and engagement analytics",
    domain: "reddit.com",
    category: "Advertising",
    isActive: true,
  },
  {
    platform: "amazon-ads",
    displayName: "Amazon Ads",
    description: "Sponsored products, display ads, and e-commerce performance",
    domain: "advertising.amazon.com",
    category: "Advertising",
    isActive: true,
  },

  // ── SEO (Ahrefs ACTIVE post Story 20.1; Semrush + Moz coming-soon) ──
  {
    platform: "ahrefs",
    displayName: "Ahrefs",
    description: "Domain rating, backlinks, organic traffic, and keyword research",
    domain: "ahrefs.com",
    category: "SEO",
    isActive: true,
  },
  {
    platform: "semrush",
    displayName: "Semrush",
    description: "Keyword rankings, competitive analysis, and site audit reports",
    domain: "semrush.com",
    category: "SEO",
    isActive: true,
  },
  {
    platform: "moz",
    displayName: "Moz",
    description: "Domain authority, page authority, spam score, and Brand Authority",
    domain: "moz.com",
    category: "SEO",
    isActive: true,
  },

  // ── Call Tracking (1 active, 1 coming-soon) ────────────────────────
  {
    platform: "callrail",
    displayName: "CallRail",
    description: "Call tracking, attribution, and lead analytics",
    domain: "callrail.com",
    category: "Call Tracking",
    isActive: true,
  },
  {
    platform: "calltrackingmetrics",
    displayName: "CallTrackingMetrics",
    description: "Call analytics, conversation intelligence, and attribution",
    domain: "calltrackingmetrics.com",
    category: "Call Tracking",
    isActive: true,
  },

  // ── Reviews (1 active, 2 coming-soon) ─────────────────────────────
  {
    platform: "google-reviews",
    displayName: "Google Reviews",
    description: "Business reviews, ratings, and reputation monitoring",
    domain: "google.com",
    category: "Reviews",
    // Story 23.1 — adapter live; shares Google OAuth with GBP.
    isActive: true,
  },
  {
    platform: "trustpilot",
    displayName: "Trustpilot",
    description: "Customer reviews, trust scores, and feedback analytics",
    domain: "trustpilot.com",
    category: "Reviews",
    isActive: true,
  },
  {
    platform: "yelp",
    displayName: "Yelp",
    description: "Business reviews, local search visibility, and customer feedback",
    domain: "yelp.com",
    category: "Reviews",
    // Story 23.5 — Yelp shipped as SEVENTH per-user-API-key family with full
    // 24/24 metrics + 21/21 dimensions catalogue coverage. Connect handler
    // routes through YelpApiKeyDialog (single-step API Key + business URL).
    isActive: true,
  },

  // ── CRM ─────────────────────────────────────────────────────────
  {
    platform: "hubspot",
    displayName: "HubSpot",
    description: "CRM contacts, deals pipeline, and marketing automation",
    domain: "hubspot.com",
    category: "CRM",
    isActive: true,
  },
];

/**
 * Category tabs for the Integration Picker.
 * "All" shows all platforms; others filter by PickerCategory.
 */
export const PICKER_CATEGORIES = [
  "All",
  "Google",
  "Social Media",
  "Advertising",
  "SEO",
  "Email & Commerce",
  "Call Tracking",
  "Reviews",
  "CRM",
] as const;

export type PickerCategoryTab = (typeof PICKER_CATEGORIES)[number];

/**
 * Custom logo overrides for platforms whose favicons are indistinguishable.
 * Google Ads and GBP return the same generic "G" favicon as google.com,
 * so we use local SVGs. GA4 and GSC have distinct favicons via S2, but we
 * override them too with higher-quality icons for consistency.
 */
/**
 * Locally-vendored logos, keyed by platform domain. EVERY domain in
 * PICKER_PLATFORMS resolves here — no runtime dependency on Google's favicon
 * service for supported platforms. Serving from our own origin means the hero
 * arch and every in-app integration logo paint instantly (no cross-origin
 * fetch, no pop-in) and the product stays self-contained (build-to-sell: no
 * undocumented external endpoint, no visitor request leaked to Google).
 *
 * The first four Google-product icons + Ahrefs are hand-curated PNGs (named by
 * platform slug). The rest are 64px favicons vendored from Google's S2 service
 * (named by sanitized domain) — regenerate any stale one with
 * `apps/web/scripts/vendor-platform-icons.sh` (render the output before
 * committing; a favicon service can return a 16px or generic-globe image).
 *
 * INVARIANT: adding a platform to PICKER_PLATFORMS requires adding its domain
 * here. `integrations.test.ts` enforces this — a missing entry fails CI, so we
 * never silently fall back to the external service again.
 */
const CUSTOM_LOGOS: Record<string, string> = {
  // Google products + Ahrefs — hand-curated icons.
  "analytics.google.com": "/assets/platform-icons/google-analytics-4.png",
  "search.google.com": "/assets/platform-icons/google-search-console.png",
  "ads.google.com": "/assets/platform-icons/google-ads.png",
  "business.google.com": "/assets/platform-icons/google-business-profile.png",
  "ahrefs.com": "/assets/platform-icons/ahrefs.png",
  // Vendored 64px favicons (named by sanitized domain).
  "ads.microsoft.com": "/assets/platform-icons/ads-microsoft-com.png",
  "advertising.amazon.com": "/assets/platform-icons/advertising-amazon-com.png",
  "callrail.com": "/assets/platform-icons/callrail-com.png",
  "calltrackingmetrics.com": "/assets/platform-icons/calltrackingmetrics-com.png",
  "facebook.com": "/assets/platform-icons/facebook-com.png",
  "google.com": "/assets/platform-icons/google-com.png",
  "hubspot.com": "/assets/platform-icons/hubspot-com.png",
  "instagram.com": "/assets/platform-icons/instagram-com.png",
  "klaviyo.com": "/assets/platform-icons/klaviyo-com.png",
  "linkedin.com": "/assets/platform-icons/linkedin-com.png",
  "mailchimp.com": "/assets/platform-icons/mailchimp-com.png",
  "moz.com": "/assets/platform-icons/moz-com.png",
  "pagespeed.web.dev": "/assets/platform-icons/pagespeed-web-dev.png",
  "pinterest.com": "/assets/platform-icons/pinterest-com.png",
  "reddit.com": "/assets/platform-icons/reddit-com.png",
  "semrush.com": "/assets/platform-icons/semrush-com.png",
  "shopify.com": "/assets/platform-icons/shopify-com.png",
  "snapchat.com": "/assets/platform-icons/snapchat-com.png",
  "tiktok.com": "/assets/platform-icons/tiktok-com.png",
  "trustpilot.com": "/assets/platform-icons/trustpilot-com.png",
  "yelp.com": "/assets/platform-icons/yelp-com.png",
  "youtube.com": "/assets/platform-icons/youtube-com.png",
};

/**
 * Generate a logo URL for a platform domain. Every supported platform resolves
 * to a locally-vendored asset (see CUSTOM_LOGOS). The Google S2 favicon service
 * remains only as a last-resort fallback for an unknown/un-vendored domain — a
 * guardrail test ensures no PICKER_PLATFORMS domain ever hits it.
 */
export function logoUrl(domain: string): string {
  return CUSTOM_LOGOS[domain] ?? `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
}

/**
 * Resolve a platform id (e.g. "google-analytics-4", stored on widget configs as
 * `integrationId`) to its display name + logo URL for the on-widget integration
 * badge. Returns null for an unknown / empty id.
 */
export function platformBadge(platform: string | undefined): { displayName: string; logoSrc: string } | null {
  if (!platform) return null;
  const p = PICKER_PLATFORMS.find((pp) => pp.platform === platform);
  if (!p) return null;
  return { displayName: p.displayName, logoSrc: logoUrl(p.domain) };
}

/**
 * All active platform keys for sync verification.
 */
export const ACTIVE_PLATFORM_KEYS: string[] = PICKER_PLATFORMS
  .filter((p) => p.isActive)
  .map((p) => p.platform);

/** Total count of supported integrations. Derived from PICKER_PLATFORMS — single source of truth. */
export const TOTAL_INTEGRATIONS = PICKER_PLATFORMS.length;

/**
 * The 9 ad-spend platforms a markup OVERRIDE may target (Story 17.2).
 *
 * DUPLICATED from `AD_SPEND_PLATFORMS` in
 * packages/db/src/schema/client-markups.ts because apps/web cannot depend on
 * @provenleap/db (rootDir constraint — same reason widgetDataForms.tsx
 * duplicates report-layout-types). SYNC: if a platform is added/removed there,
 * mirror it here. The server's Zod layer is the authority that rejects
 * unknown overrides; this list only decides which override ROWS the dialog
 * renders.
 */
export const AD_SPEND_PLATFORMS = [
  "google-ads",
  "facebook-ads",
  "instagram-ads",
  "linkedin-ads",
  "microsoft-ads",
  "pinterest-ads",
  "snapchat-ads",
  "reddit-ads",
  "amazon-ads",
] as const;

/**
 * Typed set of valid IntegrationProvider values for type guard.
 * Derives from the IntegrationProvider union type to ensure compile-time safety.
 */
const INTEGRATION_PROVIDERS: ReadonlySet<IntegrationProvider> = new Set<IntegrationProvider>([
  "google-analytics-4",
  "google-search-console",
  "google-ads",
  "google-business-profile",
  "youtube-analytics",
  "google-pagespeed",
  "facebook-pages",
  "facebook-ads",
  "instagram-business",
  "instagram-ads",
  "linkedin-pages",
  "linkedin-ads",
  "tiktok-business",
  "microsoft-ads",
  "pinterest-ads",
  "snapchat-ads",
  "reddit-ads",
  "amazon-ads",
  "mailchimp",  "klaviyo",
  "shopify",
  "hubspot",
  "ahrefs",
  "semrush",
  "moz",
  "callrail",
  "calltrackingmetrics",
  "trustpilot",
  "yelp",
]);

/**
 * Type guard to check if a platform string is an IntegrationProvider.
 * Uses a typed Set<IntegrationProvider> so the guard is sound — adding/removing
 * values from IntegrationProvider union will cause a compile error here.
 */
export function isIntegrationProvider(platform: string): platform is IntegrationProvider {
  return INTEGRATION_PROVIDERS.has(platform as IntegrationProvider);
}
